/**
 * iNaturalist public API — no key required.
 * https://api.inaturalist.org/v1/docs/
 */

export interface InatObservationPin {
  id: number;
  name: string;
  lat: number;
  lng: number;
  observedOn: string | null;
  url: string;
}

interface InatApiResult {
  id: number;
  species_guess?: string;
  taxon?: { name?: string; preferred_common_name?: string };
  location?: string;
  geojson?: { coordinates?: [number, number] };
  observed_on?: string;
  uri?: string;
}

function parseCoords(r: InatApiResult): { lat: number; lng: number } | null {
  if (r.geojson?.coordinates && Array.isArray(r.geojson.coordinates)) {
    const [lng, lat] = r.geojson.coordinates;
    if (typeof lat === 'number' && typeof lng === 'number') return { lat, lng };
  }
  if (r.location && typeof r.location === 'string') {
    const parts = r.location.split(',');
    if (parts.length >= 2) {
      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng };
    }
  }
  return null;
}

function mapResultsToPins(results: InatApiResult[]): InatObservationPin[] {
  const out: InatObservationPin[] = [];
  for (const r of results) {
    const c = parseCoords(r);
    if (!c) continue;
    const name =
      r.taxon?.preferred_common_name ||
      r.taxon?.name ||
      r.species_guess ||
      'Observation';
    out.push({
      id: r.id,
      name,
      lat: c.lat,
      lng: c.lng,
      observedOn: r.observed_on ?? null,
      url: r.uri ?? `https://www.inaturalist.org/observations/${r.id}`,
    });
  }
  return out;
}

export async function fetchObservationsInBounds(
  nelat: number,
  nelng: number,
  swlat: number,
  swlng: number,
  options?: { signal?: AbortSignal; perPage?: number }
): Promise<InatObservationPin[]> {
  const perPage = Math.min(options?.perPage ?? 60, 100);
  const params = new URLSearchParams({
    nelat: String(nelat),
    nelng: String(nelng),
    swlat: String(swlat),
    swlng: String(swlng),
    per_page: String(perPage),
    order: 'desc',
    order_by: 'created_at',
    quality_grade: 'research',
  });
  const url = `https://api.inaturalist.org/v1/observations?${params}`;
  const res = await fetch(url, { signal: options?.signal });
  if (!res.ok) throw new Error(`iNat ${res.status}`);
  const data = (await res.json()) as { results?: InatApiResult[] };
  return mapResultsToPins(data.results ?? []);
}

/** Research-grade observations of a taxon within a bounding box (route corridor). */
export async function fetchObservationsByTaxonInBounds(
  taxonId: number,
  nelat: number,
  nelng: number,
  swlat: number,
  swlng: number,
  options?: { signal?: AbortSignal; perPage?: number }
): Promise<InatObservationPin[]> {
  const perPage = Math.min(options?.perPage ?? 12, 30);
  const params = new URLSearchParams({
    taxon_id: String(taxonId),
    nelat: String(nelat),
    nelng: String(nelng),
    swlat: String(swlat),
    swlng: String(swlng),
    per_page: String(perPage),
    order: 'desc',
    order_by: 'observed_on',
    quality_grade: 'research',
  });
  const url = `https://api.inaturalist.org/v1/observations?${params}`;
  const res = await fetch(url, { signal: options?.signal });
  if (!res.ok) throw new Error(`iNat ${res.status}`);
  const data = (await res.json()) as { results?: InatApiResult[] };
  return mapResultsToPins(data.results ?? []);
}

export async function fetchTaxonDefaultPhotoUrl(
  taxonId: number
): Promise<string | null> {
  try {
    const res = await fetch(
      `https://api.inaturalist.org/v1/taxa/${taxonId}`
    );
    if (!res.ok) return null;
    const data = (await res.json()) as {
      results?: Array<{ default_photo?: { medium_url?: string } }>;
    };
    return data.results?.[0]?.default_photo?.medium_url ?? null;
  } catch {
    return null;
  }
}
